import { useState, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { useTagUserMutation } from "common/user/tag";
import AlertMessage from "common/core/alert_message";
import Link from "common/core/link";
import { captureException } from "util/exception";
import type { ProofPortal } from "constants/app_subdomains";
import { proofRebrandUrl } from "constants/marketing";

import type { ProofFrame_viewer_user as User } from "../index.query.graphql";

type Phase2RebrandOrgBannerProps = {
  user: User;
  portal: ProofPortal;
};

const REBRAND_ORG_TAG = "proof-phase-2-org-banner";
const SUPPORTED_PORTALS = ["title", "lender", "business"];

function getReactIntlBannerMessageValues(isProof: boolean) {
  const url = isProof
    ? `${proofRebrandUrl}?utm_source=proof&utm_medium=banner&utm_campaign=proof-transition&utm_content=proof`
    : `${proofRebrandUrl}?utm_source=proof&utm_medium=banner&utm_campaign=proof-transition&utm_content=notarize`;
  return {
    learnMore: (text: ReactNode) => (
      <>
        <Link white href={url}>
          {text}
        </Link>
      </>
    ),
  };
}

export function usePortalMappingBannerMessage() {
  if (window.location.hostname.endsWith("proof.com")) {
    return (
      <FormattedMessage
        id="defbdc1f-7daa-4c82-b326-7714e16b43cb"
        defaultMessage={
          "Welcome to Proof! We'll update with our new name, logo, and colors in September. <learnMore>Learn more</learnMore>."
        }
        values={getReactIntlBannerMessageValues(true)}
      />
    );
  }
  return (
    <FormattedMessage
      id="069f1f3c-e100-41f3-8dc3-1a600cbb145e"
      defaultMessage={
        "The Notarize platform is rebranding to Proof in September! <learnMore>Here's what you need to know</learnMore>."
      }
      values={getReactIntlBannerMessageValues(false)}
    />
  );
}

export function Phase2RebrandOrgBanner({ user, portal }: Phase2RebrandOrgBannerProps) {
  const bannerMessage = usePortalMappingBannerMessage();
  const { id: userId } = user;
  const bannerTags = user.tags?.filter((tag) => tag?.tag === REBRAND_ORG_TAG) || [];
  const [showBanner, setShowBanner] = useState(
    Boolean(bannerTags.length === 0) && SUPPORTED_PORTALS.includes(portal),
  );

  const tagUserMutateFn = useTagUserMutation();
  const handleDismissBanner = () => {
    setShowBanner(false);
    try {
      tagUserMutateFn({
        variables: {
          input: {
            userId,
            tag: REBRAND_ORG_TAG,
          },
          userTagList: [REBRAND_ORG_TAG],
        },
      });
    } catch {
      captureException(new Error(`Error tagging rebrand org banner for user ${userId}`));
    }
  };

  return showBanner ? (
    <AlertMessage banner kind="announcement" centerText onClose={handleDismissBanner}>
      {bannerMessage}
    </AlertMessage>
  ) : null;
}
